import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Truck,
  Search,
  Printer,
  MapPin,
  Filter,
  Factory,
  Package,
  Clock,
} from "lucide-react";

import { formatMoney } from "../utils/formatters";
import ProductionConversionModal from "../components/modals/ProductionConversionModal";
import OrderEditModal from "../components/modals/OrderEditModal";
import OrderMoveModal from "../components/modals/OrderMoveModal";
import InvoiceGenerationModal from "../components/modals/InvoiceGenerationModal";
import DesvioMotivoModal from "../components/production/DesvioMotivoModal";
import PrazoSimulatorModal from "../components/modals/PrazoSimulatorModal";
import { desvioEventosService } from "../services/desvioEventosService";
import { isMonitored } from "../config/monitoredStatuses";
import { generateCertificatePDF } from "../utils/certificateGenerator";
import { useOrderProcessing } from "../hooks/useOrderProcessing";
import { useProductionContext } from "../contexts/ProductionContext";
import { useOrderActions } from "../hooks/useOrderActions";
import { useAuth } from "../contexts/AuthContext";
import TransitToggle from "../components/production/TransitToggle";
import { productionService } from "../services/productionService";
import { logAction, MODULES, getSafeUser } from "../services/logService";
import { useWhatsApp } from "../hooks/useWhatsApp";
import OrderGroupCard from "../components/production/OrderGroupCard";
import AgeChart from "../components/dashboard/AgeChart"; // <--- Add AgeChart
import {
  LOGISTICS_STATUS_CONFIG,
  LOGISTICS_ORDER,
} from "../config/logisticsStatuses";
import {
  PRODUCTION_STATUS_CONFIG,
  KANBAN_ORDER,
} from "../config/productionStatuses";
import { canArchiveOrder, canDeleteOrder } from "../utils/logisticsLogic";

// Helper Anti-ND
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A";
};

export default function OrdersTab({ findCatalogItem }) {
  // --- ESTADOS ---
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [selectedOrders, setSelectedOrders] = useState(new Set()); // <--- Changed from Items to Orders
  const [searchTerm, setSearchTerm] = useState("");

  // Filtros Globais
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [filterUF, setFilterUF] = useState("all");
  const [filterProdStatus, setFilterProdStatus] = useState("all");
  const [ageFilter, setAgeFilter] = useState(null); // <--- Add Age Filter

  const { user } = useAuth();
  
  // Dados
  const { orders: rawData, setOrders: setRawData } = useProductionContext();

  const actions = useOrderActions(user, setRawData);

  const { sendWhatsAppMessage } = useWhatsApp();

  // Modais
  const [editingItem, setEditingItem] = useState(null);
  const [movingItem, setMovingItem] = useState(null);
  const [editingOrderGroup, setEditingOrderGroup] = useState(null);
  const [emittingOrderGroup, setEmittingOrderGroup] = useState(null);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

  // Estados do Controle de Qualidade
  const [desvioModalOpen, setDesvioModalOpen] = useState(false);
  const [desvioStatusDestino, setDesvioStatusDestino] = useState(null);
  const [desvioOrdersList, setDesvioOrdersList] = useState([]);

  // Processamento
  const groupedOrders = useOrderProcessing(
    rawData,
    searchTerm,
    "all",
    findCatalogItem
  );

  // --- HELPERS ---
  const getOrderUF = (group) => {
    if (group.customerState) return group.customerState;
    const item = group.items?.[0];
    if (!item) return "-";
    if (item.shipping?.address?.statecode)
      return item.shipping.address.statecode;
    if (item.shipping?.address?.state) return item.shipping.address.state;
    if (item.order?.shipping_address?.state)
      return item.order.shipping_address.state;
    if (item.order?.customer?.state) return item.order.customer.state;
    return "-";
  };

  const availableUFs = useMemo(() => {
    const ufs = new Set();
    groupedOrders.forEach((g) => {
      const uf = getOrderUF(g);
      if (uf && uf !== "-" && uf.length === 2) ufs.add(uf.toUpperCase());
    });
    return Array.from(ufs).sort();
  }, [groupedOrders]);

  const displayedOrders = useMemo(() => {
    let data =
      activeStatusFilter === "all"
        ? groupedOrders
        : groupedOrders.filter((g) => g.logisticsStatus === activeStatusFilter);

    if (filterUF !== "all") {
      data = data.filter((g) => getOrderUF(g) === filterUF);
    }

    if (filterProdStatus !== "all") {
      data = data.filter((g) =>
        g.items.some((i) => i.status === filterProdStatus)
      );
    }

    if (ageFilter) {
      const { getBusinessDaysDiff } = require("../utils/formatters"); // Importação inline para evitar erro de escopo topo
      data = data.filter(g => {
        // Pega a data mais antiga do grupo
        const validDates = g.items
          .map(i => i.customCreatedAt || i.createdAt)
          .filter(Boolean)
          .map(d => typeof d.toDate === "function" ? d.toDate() : new Date(d));
        
        if (validDates.length === 0) return false;
        
        const oldestDate = new Date(Math.min(...validDates.map(d => d.getTime())));
        const rawDays = getBusinessDaysDiff(oldestDate);
        const days = Math.floor(rawDays);
        
        // Aplica range
        if (ageFilter.max === undefined) {
          // 10+ dias
          return days >= ageFilter.min;
        } else {
          // Range específico
          return days >= ageFilter.min && days <= ageFilter.max;
        }
      });
    }

    return data;
  }, [groupedOrders, activeStatusFilter, filterUF, filterProdStatus, ageFilter]); // <--- dependência adicionada

  // --- DADOS PARA O GRÁFICO ---
  const chartData = useMemo(() => {
    return groupedOrders.map(g => {
      // O AgeChart precisa de customCreatedAt no objeto para funcionar.
      // Damos a ele a data do item MAIS ANTIGO do grupo
      const validDates = g.items
        .map(i => i.customCreatedAt || i.createdAt)
        .filter(Boolean)
        .map(d => typeof d.toDate === "function" ? d.toDate() : new Date(d));
      
      if (validDates.length === 0) return g;
      const oldestDate = new Date(Math.min(...validDates.map(d => d.getTime())));
      
      return {
        ...g,
        customCreatedAt: oldestDate
      };
    });
  }, [groupedOrders]);

  const statusCounts = useMemo(() => {
    return groupedOrders.reduce((acc, order) => {
      const status = order.logisticsStatus || "OUTROS";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [groupedOrders]);

  // --- ACTIONS COM useCallback (Para Memoization funcionar) ---
  const handleToggleExpand = useCallback((orderNum) => {
    const safeStr = String(orderNum);
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(safeStr)) newSet.delete(safeStr);
      else newSet.add(safeStr);
      return newSet;
    });
  }, []);

  const handleToggleSelection = useCallback((orderNumber) => {
    const safeStr = String(orderNumber);
    setSelectedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(safeStr)) newSet.delete(safeStr);
      else newSet.add(safeStr);
      return newSet;
    });
  }, []);

  const handleMoveItem = async (itemIdFromModal, formData) => {
    if (!formData) return;
    const inputNumber = formData.orderNumber;
    const inputName = formData.customerName;
    let finalName = inputName;

    if (!finalName && inputNumber) {
      const existingOrder = groupedOrders.find(
        (g) => String(g.orderNumber) === String(inputNumber)
      );
      if (existingOrder) finalName = existingOrder.customerName;
    }

    const finalData = {
      orderNumber: inputNumber || "AVULSO",
      customerName: finalName || "Cliente Balcão",
    };

    const success = await actions.moveItem(itemIdFromModal, finalData);
    if (success) setMovingItem(null);
  };

  const handleUpdateOrder = async (newData) => {
    if (!editingOrderGroup) return;
    const success = await actions.saveOrderDetails(
      editingOrderGroup.items,
      newData
    );
    if (success) setEditingOrderGroup(null);
  };

  const handleSaveEditItem = async (updatedData) => {
    try {
      console.log("💾 Salvando Edição Item (OrdersTab):", updatedData);

      // updatedData vem do Modal com: { id, specs, exception, sku, isInterceptedPE }

      // 1. Atualiza Specs, SKU e Exception
      await productionService.updateSpecs(
        updatedData.id,
        updatedData.specs,
        updatedData.exception,
        updatedData.sku,
        user?.name
      );

      // 2. Se houve mudança de status PE (Interceptação via Aviso Vital no Modal)
      // O Modal calcula isInterceptedPE = true se tiver exceção.
      // Podemos forçar essa atualização se necessário, mas o updateSpecs já grava a Exception.
      // Se o usuário quer que "Aviso Vital" force "Interceptado", o Modal já mandou a flag.
      // Mas o updateSpecs não atualiza a flag isInterceptedPE.
      // Se quisermos ser consistentes com a ProductionTab (que também não atualiza a flag via updateSpecs),
      // mantemos assim. Mas se o usuário diz que "lá funciona", talvez lá ele esteja usando o Toggle separado?
      // De qualquer forma, o log vai ajudar.

      // Log
      await logAction(
        getSafeUser(user),
        MODULES.LOGISTICA,
        "EDITAR_ITEM",
        `Editou specs/exceção do item ${updatedData.sku}`,
        {
          itemId: updatedData.id,
          specs: updatedData.specs,
          exception: updatedData.exception,
          sku: updatedData.sku
        }
      );

      setEditingItem(null);
    } catch (error) {
      console.error("Erro handleSaveEditItem:", error);
      alert("Erro ao salvar item: " + error.message);
    }
  };

  const handlePrintCertificates = async () => {
    // Agora recuperamos TODOS os itens de cada PEDIDO selecionado usando comparação segura
    const itemsToPrint = Array.from(selectedOrders)
      .flatMap((safeStrNum) => {
        const group = groupedOrders.find((g) => String(g.orderNumber) === safeStrNum);
        return group ? group.items : [];
      })
      .filter(Boolean);

    if (itemsToPrint.length === 0)
      return alert("Nenhum item válido encontrado nos pedidos selecionados para impressão.");

    const successPDF = generateCertificatePDF(itemsToPrint, findCatalogItem);
    if (successPDF) {
      await actions.markAsPrinted(itemsToPrint);
      setSelectedOrders(new Set()); // Limpa após print
    }
  };

  const handleToggleTransit = useCallback(async (item, direction) => {
    try {
      // Lógica correta de trânsito
      const newStatus = item.transit_status === direction ? null : direction;

      // 1. Atualiza no Banco (Usando a função certa de trânsito)
      await productionService.toggleTransit(item.id, newStatus);

      // 2. Log
      const actionText = newStatus
        ? `Marcou trânsito: ${newStatus === "subindo"
          ? "SUBINDO (Escritório)"
          : "DESCENDO (Fábrica)"
        }`
        : "Removeu marcação de trânsito";

      await logAction(
        getSafeUser(user),
        MODULES.LOGISTICA,
        "TRANSITO",
        `${actionText} - Item ${item.sku}`,
        { itemId: item.id, sku: item.sku, newTransitStatus: newStatus }
      );
    } catch (error) {
      console.error(error);
      alert("Erro ao alterar trânsito: " + error.message);
    }
  }, [user]);

  // Actions diretas dos hooks não recriam (se as funcoes lá nao recriarem, mas melhor wrappar por seguranca)
  const handleUpdateLogisticsStatus = useCallback((group, status) => {
    actions.updateLogisticsStatus(group, status);
  }, [actions]);

  const handleSaveDesvios = useCallback(async (eventos) => {
    try {
      if (eventos.length > 1) {
        await desvioEventosService.createEventosLote(eventos, user?.id);
      } else if (eventos.length === 1) {
        await desvioEventosService.createEvento({
          ...eventos[0],
          registrado_por: user?.id,
        });
      }
      setDesvioModalOpen(false);
      setDesvioOrdersList([]);
      setDesvioStatusDestino(null);
    } catch (e) {
      console.error("Erro ao salvar desvio(s):", e);
      alert("Erro ao gravar justificativa do desvio: " + e.message);
    }
  }, [user]);

  const handleCloseDesvioModal = useCallback(() => {
    const eventos = desvioOrdersList.map((order) => ({
      status_destino: desvioStatusDestino,
      order_id: order.id,
      sku: order.sku,
      order_number: order.order?.number || "AVULSO",
      status_anterior: order.status || "",
      problema_id: "",
      observacoes: "",
      preenchido: false,
    }));
    handleSaveDesvios(eventos);
  }, [desvioOrdersList, desvioStatusDestino, handleSaveDesvios]);

  const handleUpdateItemStatus = useCallback(async (itemId, status) => {
    const item = rawData.find((o) => o.id === itemId);
    const oldStatus = item?.status;

    await actions.updateItemStatus(itemId, status);

    if (item && oldStatus) {
      desvioEventosService.updateFluxoResumo(oldStatus, status);

      if (status === "VERIFICAR" || status === "FALHA_BANCA") {
        setDesvioOrdersList([{ ...item, status: oldStatus }]);
        setDesvioStatusDestino(status);
        setDesvioModalOpen(true);
      }
    }
  }, [actions, rawData]);

  const handleBulkUpdateLogisticsStatus = async (newStatus) => {
    if (!newStatus || selectedOrders.size === 0) return;
    
    // Confirmação para evitar mudança acidental
    const confirmMessage = `Tem certeza que deseja mover ${selectedOrders.size} pedido(s) para o status "${newStatus}"?`;
    if (!window.confirm(confirmMessage)) return;

    // Recupera os groups completos e chama o update para cada um
    const groupsToUpdate = Array.from(selectedOrders)
      .map((safeStrNum) => groupedOrders.find((g) => String(g.orderNumber) === safeStrNum))
      .filter(Boolean);

    let successCount = 0;
    for (const group of groupsToUpdate) {
      const ok = await actions.updateLogisticsStatus(group, newStatus, true);
      if (ok) successCount++;
    }

    if (successCount === groupsToUpdate.length) {
      alert(`Sucesso! ${successCount} pedido(s) movido(s) para "${newStatus}".`);
      setSelectedOrders(new Set()); // Limpa seleção quando terminar
    } else {
      alert(`Aviso: Apenas ${successCount} de ${groupsToUpdate.length} pedidos foram movidos com sucesso.`);
    }
  };

  const handleArchiveOrder = useCallback((group) => {
    actions.archiveOrder(group);
  }, [actions]);

  const handleDeleteOrder = useCallback((group) => {
    actions.deleteOrder(group);
  }, [actions]);

  const handleSendWhatsApp = useCallback((group) => {
    const customerData = group.items[0]?.order?.customer || { name: group.customerName };
    sendWhatsAppMessage(customerData, group.orderNumber, group.logisticsStatus);
  }, [sendWhatsAppMessage]);

  const handleEditItem = useCallback((item) => setEditingItem(item), []);
  const handleOpenMoveItem = useCallback((item) => setMovingItem(item), []);
  const handleEditOrderGroup = useCallback((group) => setEditingOrderGroup(group), []);
  const handleOpenInvoiceModal = useCallback((group) => setEmittingOrderGroup(group), []);

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50">
      {/* MODAIS */}
      {editingItem && (
        <ProductionConversionModal
          isOpen={!!editingItem}
          reservation={editingItem}
          isEditing={true}
          findCatalogItem={findCatalogItem}
          onClose={() => setEditingItem(null)}
          onConfirm={handleSaveEditItem}
        />
      )}
      {movingItem && (
        <OrderMoveModal
          isOpen={!!movingItem}
          item={movingItem}
          onClose={() => setMovingItem(null)}
          onConfirm={handleMoveItem}
        />
      )}
      {editingOrderGroup && (
        <OrderEditModal
          isOpen={!!editingOrderGroup}
          orderGroup={editingOrderGroup}
          onClose={() => setEditingOrderGroup(null)}
          onSave={handleUpdateOrder}
        />
      )}
      {emittingOrderGroup && (
        <InvoiceGenerationModal
          isOpen={!!emittingOrderGroup}
          orderGroup={emittingOrderGroup}
          findCatalogItem={findCatalogItem}
          onClose={() => setEmittingOrderGroup(null)}
          onSuccess={() => setEmittingOrderGroup(null)}
        />
      )}
      <DesvioMotivoModal
        isOpen={desvioModalOpen}
        statusDestino={desvioStatusDestino}
        ordersList={desvioOrdersList}
        onClose={handleCloseDesvioModal}
        onSave={handleSaveDesvios}
      />
      {isSimulatorOpen && (
        <PrazoSimulatorModal
          isOpen={isSimulatorOpen}
          onClose={() => setIsSimulatorOpen(false)}
          findCatalogItem={findCatalogItem}
        />
      )}

      {/* SIDEBAR */}
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col shrink-0 z-10 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-700 flex items-center gap-2">
            <Truck size={18} /> Logística
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
          <button
            onClick={() => setActiveStatusFilter("all")}
            className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex justify-between items-center ${activeStatusFilter === "all"
                ? "bg-slate-800 text-white shadow-md"
                : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
          >
            <span>VISÃO GERAL</span>
            <span className="bg-white/20 px-1.5 rounded text-[10px]">
              {groupedOrders.length}
            </span>
          </button>
          <div className="h-px bg-slate-200 my-2"></div>
          {LOGISTICS_ORDER.map((statusId) => {
            const config = LOGISTICS_STATUS_CONFIG[statusId];
            const count = statusCounts[statusId] || 0;
            const isActive = activeStatusFilter === statusId;
            return (
              <button
                key={statusId}
                onClick={() =>
                  setActiveStatusFilter(isActive ? "all" : statusId)
                }
                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex justify-between items-center border border-transparent ${isActive
                    ? `shadow-md ring-1 ring-slate-300 bg-white border-l-4 border-l-blue-500`
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                style={
                  isActive ? { borderLeftColor: config?.hex || "blue" } : {}
                }
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${config?.color
                        ? config.color.split(" ")[0].replace("bg-", "bg-")
                        : "bg-gray-400"
                      }`}
                  ></div>
                  <span className={isActive ? "text-slate-900" : ""}>
                    {statusId}
                  </span>
                </div>
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] ${isActive
                      ? "bg-slate-100 text-slate-700"
                      : "bg-slate-100 text-slate-500"
                    }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* MOBILE MENU */}
        <div className="md:hidden bg-slate-100 border-b border-slate-200 p-3 shrink-0 z-20">
          <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
            <Truck size={12} /> Filtrar Status Logístico:
          </label>
          <div className="relative">
            <select
              value={activeStatusFilter}
              onChange={(e) => setActiveStatusFilter(e.target.value)}
              className="w-full appearance-none bg-white border border-slate-300 text-slate-800 text-sm font-bold rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            >
              <option value="all">VISÃO GERAL ({groupedOrders.length})</option>
              {LOGISTICS_ORDER.map((statusId) => (
                <option key={statusId} value={statusId}>
                  {statusId} ({statusCounts[statusId] || 0})
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
              <Filter size={14} />
            </div>
          </div>
        </div>

        {/* TOPO */}
        <div className="bg-white border-b border-slate-200 p-4 flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center shrink-0">
          <div className="flex flex-col md:flex-row gap-3 w-full xl:w-auto flex-1">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Buscar pedido, cliente, SKU..."
                className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-full focus:border-blue-500 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:min-w-[100px]">
                <MapPin
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <select
                  className="pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm w-full outline-none appearance-none bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 font-bold text-slate-600"
                  value={filterUF}
                  onChange={(e) => setFilterUF(e.target.value)}
                >
                  <option value="all">UF</option>
                  {availableUFs.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                <Filter
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  size={12}
                />
              </div>
              <div className="relative flex-[1.5] md:min-w-[180px]">
                <Factory
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <select
                  className="pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm w-full outline-none appearance-none bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 font-bold text-slate-600"
                  value={filterProdStatus}
                  onChange={(e) => setFilterProdStatus(e.target.value)}
                >
                  <option value="all">Prod: Todos</option>
                  {KANBAN_ORDER.map((status) => (
                    <option key={status} value={status}>
                      {PRODUCTION_STATUS_CONFIG[status]?.label || status}
                    </option>
                  ))}
                </select>
                <Filter
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  size={12}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto justify-end">
            <button
              type="button"
              onClick={() => setIsSimulatorOpen(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-xs shrink-0 cursor-pointer hover:scale-[1.02]"
              title="Simular prazos de fabricação para clientes antes da compra"
            >
              <Clock size={15} />
              <span>Simular Prazos</span>
            </button>

            {selectedOrders.size > 0 && (
              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto mt-2 md:mt-0 items-center bg-blue-50/50 p-2 rounded-xl border border-blue-100 shadow-sm animate-fade-in">
                <span className="text-[10px] font-bold text-blue-800 uppercase px-2 w-full md:w-auto text-center md:text-left">
                  {selectedOrders.size} {selectedOrders.size === 1 ? 'Pedido Selecionado' : 'Pedidos Selecionados'}
                </span>
                
                <div className="flex w-full md:w-auto mt-1 md:mt-0 gap-2 items-center">
                  <select
                    className="px-3 py-2 border border-blue-200 rounded-lg text-sm outline-none bg-white font-bold text-blue-900 cursor-pointer shadow-sm w-full md:w-auto"
                    onChange={(e) => {
                      handleBulkUpdateLogisticsStatus(e.target.value);
                      e.target.value = ""; // Reseta o select após a ação
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Mover Selecionados para...</option>
                    {LOGISTICS_ORDER.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  <button
                    onClick={handlePrintCertificates}
                    className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-black transition-colors shadow-sm whitespace-nowrap justify-center w-full md:w-auto"
                  >
                    <Printer size={16} /> Certificados
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* LISTA E GRÁFICO */}
        <div className="flex-1 overflow-y-auto p-2 md:p-4 custom-scrollbar bg-slate-50/50">
          
          <AgeChart 
            orders={chartData} // <-- Passa chartData computado em vez de pure orders
            activeFilter={ageFilter}
            onBarClick={(filterParams) => {
              // Se clicar na barra já ativa, desmarca
              if (
                ageFilter && 
                ageFilter.min === filterParams.min && 
                ageFilter.max === filterParams.max
              ) {
                setAgeFilter(null);
              } else {
                setAgeFilter(filterParams);
              }
            }}
          />

          {ageFilter && displayedOrders.length > 0 && (
            <div className="mb-4 bg-blue-50 text-blue-800 p-3 rounded-xl border border-blue-200 flex justify-between items-center text-sm shadow-sm animate-fade-in">
              <div>
                Filtrando por: <strong>{ageFilter.label}</strong>{" "}
                <span className="opacity-70 text-xs ml-2">
                  ({displayedOrders.length} pedidos em foco)
                </span>
              </div>
              <button
                onClick={() => setAgeFilter(null)}
                className="text-blue-600 hover:text-blue-900 font-bold bg-white/50 px-3 py-1 rounded"
              >
                Limpar Filtro
              </button>
            </div>
          )}

          {displayedOrders.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <Package size={48} className="mx-auto mb-2 opacity-20" />
              <p>Nenhum pedido encontrado nesta visão.</p>
              {(filterUF !== "all" || filterProdStatus !== "all") && (
                <button
                  onClick={() => {
                    setFilterUF("all");
                    setFilterProdStatus("all");
                  }}
                  className="text-blue-500 hover:underline text-sm mt-2"
                >
                  Limpar filtros avançados
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6 pb-20">
              {(activeStatusFilter === "all"
                ? LOGISTICS_ORDER
                : [activeStatusFilter]
              ).map((statusLabel) => {
                const ordersInGroup = displayedOrders.filter(
                  (g) => g.logisticsStatus === statusLabel
                );
                if (ordersInGroup.length === 0) return null;

                const config = LOGISTICS_STATUS_CONFIG[statusLabel] || {};
                const headerColorClass = config.color
                  ? config.color.split(" ")[0]
                  : "bg-slate-100";
                const headerTextClass = config.color
                  ? config.color.split(" ")[1]
                  : "text-slate-700";

                return (
                  <div key={statusLabel} className="space-y-3">
                    <div
                      className={`px-4 py-2 rounded-lg flex justify-between items-center ${headerColorClass} border border-transparent shadow-sm`}
                    >
                      <div className="flex items-center gap-2">
                        <h3
                          className={`font-bold text-sm uppercase ${headerTextClass}`}
                        >
                          {statusLabel}
                        </h3>
                      </div>
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded bg-white/50 ${headerTextClass}`}
                      >
                        {ordersInGroup.length}
                      </span>
                    </div>

                    <div className="space-y-3 pl-1 md:pl-2">
                      {ordersInGroup.map((group) => (
                        <OrderGroupCard
                          user={user}
                          key={String(group.orderNumber)}
                          group={group}
                          isExpanded={expandedOrders.has(String(group.orderNumber))}
                          isSelected={selectedOrders.has(String(group.orderNumber))}
                          onToggleExpand={handleToggleExpand}
                          onToggleSelection={handleToggleSelection}
                          onEditItem={handleEditItem}
                          onMoveItem={handleOpenMoveItem}
                          onToggleTransit={handleToggleTransit}
                          onUpdateItemStatus={handleUpdateItemStatus}
                          onUpdateLogisticsStatus={handleUpdateLogisticsStatus}
                          onSendWhatsApp={handleSendWhatsApp}
                          onArchiveOrder={handleArchiveOrder}
                          onDeleteOrder={handleDeleteOrder}
                          onEditOrderGroup={handleEditOrderGroup}
                          onEmitInvoice={handleOpenInvoiceModal}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
